import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { getArticleBySlug } from "@/mocks/articles";
import type { ArticleData } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://pandabuy.rest";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

function extractDescription(article: ArticleData): string {
  const firstParagraph = article.content.find((b) => b.type === "p");
  if (firstParagraph && firstParagraph.text) {
    const clean = stripHtml(firstParagraph.text);
    return clean.length > 300 ? clean.slice(0, 297) + "..." : clean;
  }
  return article.title;
}

function parseDate(dateStr: string): string {
  const parsed = new Date(dateStr);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }
  return "2026-06-29";
}

function buildArticleSchemas(article: ArticleData, slug: string): Record<string, unknown>[] {
  const articleUrl = `${siteUrl}/article/${slug}`;
  const description = extractDescription(article);
  const isoDate = parseDate(article.date);

  return [
    {
      "@type": "Article",
      headline: article.title,
      description,
      image: article.imageUrl,
      datePublished: isoDate,
      dateModified: isoDate,
      author: {
        "@type": "Organization",
        name: "Pandabuy Spreadsheet Hub",
        url: siteUrl,
      },
      publisher: {
        "@type": "Organization",
        name: "Pandabuy Spreadsheet Hub",
        url: siteUrl,
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": articleUrl,
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Shopping Guide",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
        },
      ],
    },
  ];
}

function ArticleContent({ article }: { article: ArticleData }) {
  return article.content.map((block, i) => {
    switch (block.type) {
      case "h2":
        return <h2 key={i} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4 leading-tight scroll-mt-[100px]" id={`section-${i}`}>{block.text}</h2>;
      case "p":
        return <p key={i} className="text-foreground-600 leading-relaxed mb-5 text-base md:text-lg" dangerouslySetInnerHTML={{ __html: block.text || "" }}></p>;
      case "ul":
        return <ul key={i} className="list-disc pl-6 mb-5 space-y-2">{block.items?.map((item, j) => <li key={j} className="text-foreground-600 leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: item }}></li>)}</ul>;
      case "ol":
        return <ol key={i} className="list-decimal pl-6 mb-5 space-y-2">{block.items?.map((item, j) => <li key={j} className="text-foreground-600 leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: item }}></li>)}</ol>;
      case "table":
        return (
          <div key={i} className="overflow-x-auto my-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-background-200">{block.headers?.map((h, j) => <th key={j} className="text-left py-3 pr-4 font-semibold text-foreground-950">{h}</th>)}</tr>
              </thead>
              <tbody>
                {block.rows?.map((row, ri) => (
                  <tr key={ri} className="border-b border-background-200/70">{row.map((cell, ci) => <td key={ci} className="py-3 pr-4 text-foreground-600">{cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return null;
    }
  });
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleSchemas(article, slug || "")} />
      <Header />
      <div className="pt-[88px] bg-background-50">
        <div className="max-w-[960px] mx-auto px-6 md:px-10 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-950 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/" className="hover:text-foreground-950 transition-colors">Shopping Guide</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>
      </div>
      <article className="pb-16">
        <div className="max-w-[960px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 min-w-0">
              <header className="mb-10">
                <span className="inline-block text-xs font-medium text-accent-500 bg-accent-100/70 px-3 py-1 rounded-full mb-4">{article.category}</span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">{article.title}</h1>
                <div className="flex items-center gap-4 text-sm text-foreground-400">
                  <time dateTime={article.date}>{article.date}</time>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>{article.readTime}</span>
                </div>
              </header>
              <figure className="mb-10 rounded-2xl overflow-hidden">
                <img src={article.imageUrl} alt={article.title} className="w-full object-cover" loading="eager" />
              </figure>
              <ArticleContent article={article} />
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-1 mb-1">
                  <span className="block w-8 h-px bg-foreground-300"></span>
                  <span className="text-sm font-semibold text-foreground-950">By Pandabuy Editorial Team</span>
                </div>
                <p className="text-sm text-foreground-400">Original content created for pandabuy.rest. Updated June 2026.</p>
              </div>
            </div>
            <aside className="lg:w-[280px] shrink-0">
              <div className="lg:sticky lg:top-[88px] space-y-6">
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-3 uppercase tracking-wider">On This Page</h4>
                  <nav className="space-y-2">
                    {article.content.filter(b => b.type === "h2").map((b, i) => (
                      <a key={i} href={`#section-${article.content.indexOf(b)}`} className="block text-sm text-foreground-500 hover:text-foreground-950 transition-colors">{b.text}</a>
                    ))}
                  </nav>
                </div>
                <div className="bg-gradient-to-br from-accent-100 to-accent-50 rounded-2xl p-6">
                  <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">Get the Real Sheet</h4>
                  <p className="text-sm text-foreground-600 mb-5">Access 10,000+ curated items across all fourteen categories.</p>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center w-full px-5 py-3 text-sm font-semibold rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    Go to Main Store <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                </div>
                <Link to="/" className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer">
                  <i className="ri-arrow-left-line"></i> Back to Home
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>
      <section className="bg-background-100 py-12">
        <div className="max-w-[960px] mx-auto px-6 md:px-10 text-center">
          <p className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Want more spreadsheet finds?</p>
          <p className="text-foreground-500 text-sm mb-6">Browse all fourteen categories with daily updated curated collections.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer">
            Explore All Categories <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
}